import { makeStyles } from '@material-ui/styles'
import { imageUrlRoot } from 'app/constants'
import Button from 'core/elements/button/Button'
import Text from 'core/elements/Text'
import { routeManager } from 'core/utils/routes'
import SelectableCard from 'pf9-ui-components/built/components/SelectableCard'
import Tooltip from 'core/elements/tooltip'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import React from 'react'
import useReactRouter from 'use-react-router'
import { formatMonthlyCost } from './helpers'

interface RegionCardProps {
  region: any
  alerts: unknown[]
  onClick: (regionId: string) => void
  onViewAdminConsoleClick: (regionId: string) => void
  isAdmin?: boolean
}

const platformLogoMap = {
  openstack: `${imageUrlRoot}/opencloud/openstack-logo.png`,
  vmware: `${imageUrlRoot}/opencloud/vmware-logo.png`,
}

export default function RegionCard({
  region,
  alerts = [],
  onClick,
  onViewAdminConsoleClick,
  isAdmin = false,
}: RegionCardProps) {
  const classes = useStyles()
  const { history } = useReactRouter()
  const logo = region?.type ? platformLogoMap[region.type] : null

  const handleAlertsClick = (event: React.MouseEvent) => {
    event.stopPropagation() // Prevent the card from being selected
    history.push(routeManager.routes.opencloud.alerts.path({ region: region.name }))
  }

  const handleViewAdminConsoleClick = (event: React.MouseEvent) => {
    event.stopPropagation() // Prevent the card from being selected
    onViewAdminConsoleClick(region.name)
  }

  return (
    <SelectableCard
      id={region.name}
      title={
        <div className={classes.cardHeader}>
          <img src={logo} alt="platform logo" className={classes.logo} />
          {region?.monthlyCost?.amount !== undefined && (
            <div className={classes.costPerMonthContainer}>
              <Text variant="subtitle1">{formatMonthlyCost(region.monthlyCost)}</Text>
              <Text variant="body2">/mo</Text>
            </div>
          )}
        </div>
      }
      onClick={onClick}
    >
      <Text variant="subtitle2">{region.name}</Text>
      <div className={classes.infoContainer}>
        <div className={classes.leftColumn}>
          {region?.infra?.vcpus !== undefined && (
            <Text variant="body2">{region.infra.vcpus.toLocaleString()} vCPUs</Text>
          )}
          {region?.infra?.ram !== undefined && (
            <Text variant="body2">{region.infra.ram.toLocaleString()} GiB Memory</Text>
          )}
          {region?.infra?.disk !== undefined && (
            <Text variant="body2">{region.infra.disk.toLocaleString()} GiB Storage</Text>
          )}
          {region?.infra?.network?.vendor && region?.infra?.network?.product && (
            <Text variant="body2">
              {region.infra.network.vendor} {region.infra.network.product}
            </Text>
          )}
        </div>
        <div className={classes.rightColumn}>
          {region?.region && <Text variant="body2">{region.region} region</Text>}
        </div>
      </div>
      <div className={classes.buttons}>
        <Button
          variant="secondary"
          className={alerts.length > 0 ? classes.activeButton : classes.button}
          type="button"
          onClick={handleAlertsClick}
        >
          View Alerts
          {<span style={{ marginLeft: 4 }}>{alerts.length}</span>}
        </Button>
        {isAdmin && (
          <Button variant="secondary" onClick={handleViewAdminConsoleClick} type="button">
            View Admin Console
          </Button>
        )}
      </div>
    </SelectableCard>
  )
}

const useStyles = makeStyles<Theme>((theme: Theme) => ({
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '50px',
    padding: '16px 24px 0 24px',
  },
  logo: {
    objectFit: 'cover',
    maxHeight: '50px',
    width: '145px',
    marginRight: theme.spacing(2),
  },
  costPerMonthContainer: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gridGap: theme.spacing(1),
    alignItems: 'center',
  },
  infoContainer: {
    display: 'grid',
    gridTemplateColumns: '60% 40%',
    marginTop: theme.spacing(2),
  },
  leftColumn: {
    borderRight: `1px solid #DADCDF`,
  },
  rightColumn: {
    marginLeft: theme.spacing(4),
  },
  buttons: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, max-content)',
    gridGap: theme.spacing(2),
    marginTop: theme.spacing(4),
  },
  activeButton: {
    // @ts-expect-error ErrorRed is not added the the Theme type
    backgroundColor: theme.palette.errorRed[700],
    // @ts-expect-error ErrorRed is not added the the Theme type
    border: theme.palette.errorRed[700],
    '& > div': {
      color: `${theme.palette.common.white} !important`,
    },
    '&:hover': {
      // @ts-expect-error ErrorRed is not added the the Theme type
      backgroundColor: theme.palette.errorRed[500],
      // @ts-expect-error ErrorRed is not added the the Theme type
      border: theme.palette.errorRed[500],
      '& > div': {
        color: `${theme.palette.common.white} !important`,
      },
    },
  },
}))
